import {
  AttachFileOutlined,
  ChevronRightRounded,
  InfoOutlined,
} from "@mui/icons-material";
import { Box, Tooltip, Typography } from "@mui/material";
import { green, grey, red, yellow } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { isFunction, isNil, size } from "lodash";
import { memo, useCallback, useMemo } from "react";
import { htmlStringToText } from "../../_src_shared/utils/functions/htmlStringToText";
import AgGridTableWrapper from "../agGrid/AgGridTableWrapper";
import CellRendererRequirementType from "../agGrid/cellRenderer/CellRendererRequirementType";
import Flx from "../layout/Flx";
import CommentsRenderer from "../renderers/CommentsRenderer";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import Txt from "../typography/Txt";
import { useFileDocGroupAndTypesHook } from "../../_src_shared/hooks/useFileDocGroupAndTypesHook";
import { getDaysAgo } from "../../_src_shared/utils/functions/getDaysAgo";
import { da } from "date-fns/locale";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import { formatDateShort } from "../../utils/dates/formatDateShort";

const LoanConditionsTable = ({ rowData, onRowClicked }) => {
  const { formatDocTypeLabel } = useFileDocGroupAndTypesHook({});
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Status",
        field: "status", // ['Open', 'In Progress',  'Accepted', 'Rejected', 'Second Level Review']
        width: 92,
        // sort: "desc",
        cellStyle: { cursor: "pointer" },
        cellClass: "ag-cell-vertical-center",
      },

      {
        headerName: "Condition Type",
        field: "requirementType",
        width: 120,
        cellClass: "ag-cell-vertical-center",
        cellStyle: { cursor: "pointer" },
        cellRenderer: (params) => (
          <CellRendererRequirementType value={params.value} />
        ),
      },
      {
        headerName: "Requirement Details",
        field: "htmlString",
        minWidth: 250,
        flex: 1,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          lineHeight: 1.5,
          cursor: "pointer",
          paddingTop: 12,
          paddingBottom: 12,
          wrapText: true, // Wrap Text
          whiteSpace: "normal", // Allow normal white space handling
          autoHeight: true, // Adjust Cell Height to Fit Wrapped Text
        },
        valueFormatter: (p) => {
          if (isNil(p.value) || p.value === "") {
            return "N/A";
          }

          return htmlStringToText(p.value);
        },
        cellRenderer: (p) => {
          if (isNil(p.value) || p.value === "") {
            return "N/A";
          }
          if (p?.data?.docType) {
            return (
              <Flx column>
                <Txt bold fontSize={12}>
                  Missing Document: {formatDocTypeLabel(p.data.docType)}
                </Txt>
                <Txt sx={{ fontSize: "12px" }}>{htmlStringToText(p.value)}</Txt>
              </Flx>
            );
          }

          return htmlStringToText(p.value);
        },
      },

      {
        headerName: "Document Status",
        field: "docStipStatus", // ["Open Item", "Received - Pending Review", "Reviewed - Not Accepted", "Accepted"]
        width: 180,
        // sort: "desc",

        cellClass: "ag-cell-vertical-center",
        tooltipComponent: null,
        valueFormatter: (p) => {
          const status = p.value;

          if (isNil(status)) {
            return "N/A";
          }
          return status;
        },
        cellStyle: (params) => {
          const status = params.value;
          // let backgroundColor = green[100];
          let backgroundColor = "transparent";

          if (status === "Received - Pending Review") {
            backgroundColor = yellow[100];
          }
          if (status === "Reviewed - Not Accepted") {
            backgroundColor = red[100];
          }
          if (status === "Accepted") {
            backgroundColor = green[700];
          }
          return {
            cursor: "pointer",
            // opacity: isNil(status) ? 0.65 : 1,
            fontStyle: isNil(status) ? "italic" : "normal",
            backgroundColor,
          };
        },
      },

      {
        headerName: "Last Updated",
        field: "updateDateTime",
        sort: "desc",
        width: 130,
        cellClass: "ag-cell-vertical-center",
        tooltipComponent: (params) => (
          <Flx
            ac
            g={1}
            sx={{
              background: "#ffffff",
              border: `1px solid ${grey[300]}`,
              p: 1,
              borderRadius: 2,
            }}
          >
            Last Updated <b>{formatDateTime(params.data?.updateDateTime)}</b> (
            {getDaysAgo(params.data?.updateDateTime) === 0
              ? "Today"
              : `${getDaysAgo(params.data?.updateDateTime)} days ago`}
            )
          </Flx>
        ),
        tooltipValueGetter: (params) => {
          return params.data?.updateDateTime;
        },
        cellRenderer: (p) => {
          const daysAgo = getDaysAgo(p?.value);
          let text = "";
          if (daysAgo === 0) {
            text = "Today";
          } else {
            text = `${daysAgo} days ago`;
          }

          if (daysAgo > 60) {
            text = formatDateShort(p?.value);
          }

          return (
            <Flx ac g={0.4}>
              {text}
              <InfoOutlined
                className="thin-9"
                sx={{ color: "inherit", fontSize: "16px" }}
              />
            </Flx>
          );
          // return formatDateTime(p?.value);
          // return formatDateTime(p?.value);
        },
      },
      {
        width: 150,
        headerName: "Interactions",
        cellClass: "ag-cell-vertical-center",
        cellStyle: { cursor: "pointer" },
        cellRenderer: (p) => {
          return (
            <Flx sx={{ height: "100%" }} center g={3}>
              <DocumentIdsCountBadge
                count={size(p.data?.uploadedDocumentIds)}
              />
              <CommentsRenderer
                onlyCount
                comments={p.data?.comments || p.data?.conditionComments}
              />
              <ChevronRightRounded sx={{ color: grey[600] }} />
            </Flx>
          );
        },
      },
    ],
    [formatDocTypeLabel],
  );

  const getRowId = useCallback((params) => params.data._id, []);

  const getRowStyle = useCallback((params) => {
    const status = params.data?.status;
    if (status === "Open") {
      return null;
    }
    if (status === "Accepted") {
      return {
        backgroundColor: green[50],
        borderTop: `1px solid #ffffff`,
        borderBottom: `1px solid #ffffff`,
      };
      //   return { backgroundColor: "#e8f5e9a9" };
    }
    if (status === "Rejected") {
      return {
        backgroundColor: red[50],
        borderTop: `1px solid #ffffff`,
        borderBottom: `1px solid #ffffff`,
      };
    }

    return {
      backgroundColor: yellow[50],
      borderTop: `1px solid #ffffff`,
      borderBottom: `1px solid #ffffff`,
    };
  }, []);

  const defaultColDef = useMemo(
    () => ({
      cursor: "pointer",
      tooltipComponent: () => (
        <Flx
          ac
          g={1}
          sx={{
            background: "#ffffff",
            border: `1px solid ${grey[300]}`,
            p: 1,
            borderRadius: 2,
          }}
        >
          Click row to address Loan Condition
          <ChevronRightRounded sx={{ color: grey[600] }} />
        </Flx>
      ),
      tooltipValueGetter: () => "Click to address condition",
      //   resizable: true, // Enable resizing
      //   initialWidth: 200, // Optional: Set a default size
      //   wrapText: true, // Wrap Text
      //   whiteSpace: "normal", // Allow normal white space handling
      //   autoHeight: true, // Adjust Cell Height to Fit Wrapped Text
    }),
    [],
  );
  return (
    <Box
      sx={{
        // maxHeight: "800px",
        overflowY: "auto",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: "8px",
        ".ag-row-last": {
          borderBottom: "none !important",
        },
        ".ag-header-viewport": {
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <AgGridTableWrapper
        height="100%"
        simpleTable
        suppressMinHeightWhenPopulated={rowData}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          getRowId={getRowId}
          defaultColDef={defaultColDef}
          getRowStyle={getRowStyle}
          onRowClicked={onRowClicked}
          domLayout="autoHeight"
          tooltipShowDelay={100}
          enableCellTextSelection
          tooltipMouseTrack
        />
      </AgGridTableWrapper>
    </Box>
  );
};

const DocumentIdsCountBadge = memo(({ count, onClick }) => {
  return (
    <Tooltip
      title={
        isFunction(onClick)
          ? `${count} ${count === 1 ? "comment" : "comments"} — click to expand`
          : undefined
      }
    >
      <Box
        onClick={onClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: onClick ? "pointer" : "default",
          color: count > 0 ? "primary.main" : "text.secondary",
          "&:hover": onClick ? { color: "primary.dark" } : undefined,
        }}
      >
        <AttachFileOutlined sx={{ fontSize: 14 }} />
        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.5 }}>
          {count}
        </Typography>
      </Box>
    </Tooltip>
  );
});

export default LoanConditionsTable;
